import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Star } from "lucide-react";
import { IMAGES } from "@/data/classes";
import Seo from "@/components/Seo";

const TESTIMONIALS = [
  {
    quote: "Hands-on from the first minute. Our whole team left feeling actually prepared — not just recertified.",
    who: "Clinic Manager",
    context: "DFW medical practice",
  },
  {
    quote: "The instructor took real time with each of us on the manikins. I finally feel confident with an AED.",
    who: "Registered Nurse",
    context: "BLS class attendee",
  },
  {
    quote: "Rhythm Check came on-site and trained 40 staff in a single Saturday. Zero disruption to our week.",
    who: "Operations Director",
    context: "DFW warehouse client",
  },
  {
    quote: "The ACLS megacode scenarios were more realistic than any class I've taken before. Genuinely useful.",
    who: "Emergency Physician",
    context: "ACLS renewal",
  },
  {
    quote: "As a parent, the Heartsaver course was worth every minute. Practical, clear, and not intimidating.",
    who: "Parent & Coach",
    context: "Heartsaver CPR/First Aid",
  },
  {
    quote: "They tailored the workplace safety day around our specific hazards. It didn't feel generic at all.",
    who: "Safety Manager",
    context: "Manufacturing client",
  },
];

export default function Testimonials() {
  return (
    <div>
      <Seo
        title="Testimonials"
        path="/testimonials"
        description="What healthcare professionals, medical practices, and DFW businesses say after training with Rhythm Check Training."
      />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#d90429] mb-4">Testimonials</div>
            <h1 className="font-display text-5xl md:text-6xl font-black text-[#0a0a0a] leading-none">
              What attendees say.
            </h1>
            <p className="mt-6 text-lg text-gray-700 max-w-xl">
              Verified feedback from healthcare pros, business owners, and community
              members who&apos;ve trained with us.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img
              src={IMAGES.testimonial_doctor}
              alt="Healthcare professional"
              className="w-full h-80 object-cover rounded-lg border border-gray-200"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#f9fafb] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="bg-white border border-gray-200 rounded-lg p-8 hover-lift flex flex-col"
            >
              <Quote className="w-8 h-8 text-[#d90429]" />
              <div className="mt-4 flex gap-1 text-[#d90429]">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-[#d90429]" />
                ))}
              </div>
              <blockquote className="mt-4 text-gray-800 text-lg leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 pt-4 border-t border-gray-200">
                <div className="font-display font-bold text-[#0a0a0a]">— {t.who}</div>
                <div className="text-sm text-gray-500">{t.context}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-[#d90429]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-wrap gap-8 justify-between items-center">
          <h2 className="font-display text-3xl md:text-4xl font-black text-white leading-tight max-w-2xl">
            Join hundreds of DFW professionals who trained with us.
          </h2>
          <Link
            to="/schedule"
            data-testid="testimonials-signup-cta"
            className="inline-flex items-center gap-2 rounded-md bg-white px-8 py-4 text-base font-bold text-[#d90429] hover:bg-black hover:text-white transition-colors"
          >
            Sign Up for a Class <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
